'use client';

import React, { useMemo, useState, useRef } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { TvlDataPoint } from '@/types/defi';
import { formatDefiNumber } from '@/hooks/useDefiLlamaData';
import { getChain, DEFAULT_CHAIN_ID } from '@/lib/chains';

/**
 * Theme-tuned logo variants. Abstract is the one chain with a light/dark PAIR in `public/`
 * (the app icons this chart has rendered since it was Abstract-only) — every other chain has
 * the single registry `logo`, so both slots resolve to that. Keyed by chain id, not baked in,
 * so a future chain that ships a pair only has to add a row.
 */
const THEMED_CHAIN_LOGOS: Record<string, { dark: string; light: string }> = {
  abstract: { dark: '/Abstract_AppIcon_DarkMode.webp', light: '/Abstract_AppIcon_LightMode.webp' },
};

interface ChainTvlChartProps {
  data: TvlDataPoint[];
  currentTvl?: number;
  tvlChange24h?: number;
  tvlChange7d?: number;
  /**
   * The LENS this chart is rendering — drives the label, logo and DeFiLlama link. Omitted
   * ⇒ Abstract, matching `useDefiLlamaData(undefined)`, which resolves to the
   * `defi_ecosystem/current` Abstract alias. The caller must pass the same chain it fed
   * `useDefiLlamaData`, or the branding and the numbers describe different chains — which
   * is precisely the bug this prop exists to close.
   */
  chainId?: string;
  className?: string;
}

/**
 * Chain TVL Chart — 30-day historical TVL for ONE chain lens, with hover interaction.
 *
 * ⚠ Was `AbstractTvlChart`, and the name was load-bearing in the worst way: the label
 * ("Abstract TVL"), the logo and the DeFiLlama link were all hardcoded to Abstract while
 * the caller had long since started feeding it the LENS's chain-scoped TVL from
 * `defi_ecosystem/<chainId>`. /avax/market rendered Avalanche's real $418M under Abstract's
 * name and logo, linking to Abstract's DeFiLlama page (2026-08-14). Everything chain-specific
 * now comes from the registry (`shared/config/chains.js`) keyed by `chainId`, so branding and
 * numbers cannot drift apart again.
 */
export function ChainTvlChart({
  data,
  currentTvl,
  tvlChange24h,
  tvlChange7d,
  chainId,
  className = ''
}: ChainTvlChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const chartRef = useRef<SVGSVGElement>(null);

  // ── everything chain-specific, resolved from the registry ──────────────────────
  const chain = getChain(chainId) ?? getChain(DEFAULT_CHAIN_ID);
  const chainLabel = chain?.label ?? 'Abstract';
  const logos = (chain && THEMED_CHAIN_LOGOS[chain.id]) ?? {
    dark: chain?.logo ?? '',
    light: chain?.logo ?? '',
  };
  // DeFiLlama canonicalizes chain paths to LOWERCASE — `/chain/Avalanche` 307s to
  // `/chain/avalanche` — so lowercase the registry's display-cased `feeds.tvl` slug to link
  // straight at the canonical page. A chain with no TVL feed (the NFT-only registry entries)
  // gets DeFiLlama's root rather than a fabricated chain path.
  const tvlSlug = chain?.feeds?.tvl;
  const defiLlamaHref = tvlSlug
    ? `https://defillama.com/chain/${tvlSlug.toLowerCase()}`
    : 'https://defillama.com';

  // Chart dimensions - use consistent coordinate system
  const viewBoxWidth = 400;
  const viewBoxHeight = 180;
  const padding = { top: 8, right: 12, bottom: 20, left: 12 };

  const chartData = useMemo(() => {
    if (!data || data.length < 2) return null;

    const tvlValues = data.map(d => d.tvl);
    const minTvl = Math.min(...tvlValues);
    const maxTvl = Math.max(...tvlValues);
    // Add 10% padding to make the line use more vertical space
    const rangePadding = (maxTvl - minTvl) * 0.1 || maxTvl * 0.05;
    const adjustedMin = minTvl - rangePadding;
    const adjustedMax = maxTvl + rangePadding;
    const range = adjustedMax - adjustedMin || 1;

    const chartWidth = viewBoxWidth - padding.left - padding.right;
    const chartHeight = viewBoxHeight - padding.top - padding.bottom;

    const points = data.map((point, index) => {
      const x = padding.left + (index / (data.length - 1)) * chartWidth;
      const y = padding.top + chartHeight - ((point.tvl - adjustedMin) / range) * chartHeight;
      return { x, y, ...point };
    });

    // Create path for line
    const linePath = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`)
      .join(' ');

    // Create path for area fill
    const areaPath = `${linePath} L ${points[points.length - 1].x},${viewBoxHeight - padding.bottom} L ${padding.left},${viewBoxHeight - padding.bottom} Z`;

    return { points, linePath, areaPath, minTvl: adjustedMin, maxTvl: adjustedMax };
  }, [data]);

  // Determine trend
  const isPositive = useMemo(() => {
    if (!data || data.length < 2) return true;
    return data[data.length - 1].tvl >= data[0].tvl;
  }, [data]);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!chartRef.current || !chartData) return;

    const rect = chartRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * viewBoxWidth;

    // Find closest point
    const chartWidth = viewBoxWidth - padding.left - padding.right;
    const relativeX = (x - padding.left) / chartWidth;
    const index = Math.round(relativeX * (data.length - 1));

    if (index >= 0 && index < data.length) {
      setHoveredIndex(index);
    }
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!chartData || !data || data.length < 2) {
    return (
      <div className={`flex items-center justify-center h-40 bg-gray-100 dark:bg-neutral-800/50 rounded-xl ${className}`}>
        <span className="text-sm text-gray-500 dark:text-neutral-500">No TVL data available</span>
      </div>
    );
  }

  const hoveredPoint = hoveredIndex !== null ? chartData.points[hoveredIndex] : null;
  const strokeColor = isPositive ? '#84cc16' : '#ef4444';
  const TrendIcon = isPositive ? TrendingUp : tvlChange24h === 0 ? Minus : TrendingDown;

  return (
    <div className={`bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl p-3 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <img
              src={logos.dark}
              alt={chainLabel}
              className="w-5 h-5 rounded hidden dark:block"
            />
            <img
              src={logos.light}
              alt={chainLabel}
              className="w-5 h-5 rounded dark:hidden"
            />
            <span className="text-xs font-mono uppercase tracking-wider text-gray-500 dark:text-neutral-500">
              {chainLabel} TVL
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-mono font-bold text-gray-900 dark:text-white">
              {formatDefiNumber(hoveredPoint?.tvl ?? currentTvl ?? data[data.length - 1].tvl)}
            </span>
            {tvlChange24h !== undefined && hoveredIndex === null && (
              <span className={`flex items-center gap-1 text-sm font-mono ${isPositive ? 'text-lime-600 dark:text-lime-400' : 'text-red-600 dark:text-red-400'}`}>
                <TrendIcon size={14} />
                {tvlChange24h >= 0 ? '+' : ''}{tvlChange24h.toFixed(2)}%
              </span>
            )}
          </div>
          {/* Always reserve space for date to prevent layout shift */}
          <span className="text-xs font-mono text-gray-500 dark:text-neutral-500 h-4">
            {hoveredPoint ? formatDate(hoveredPoint.date) : '\u00A0'}
          </span>
        </div>

        {/* Period badges */}
        <div className="flex flex-col gap-1 text-right">
          {tvlChange24h !== undefined && (
            <span className={`text-xs font-mono ${tvlChange24h >= 0 ? 'text-lime-600 dark:text-lime-400' : 'text-red-600 dark:text-red-400'}`}>
              24h: {tvlChange24h >= 0 ? '+' : ''}{tvlChange24h.toFixed(1)}%
            </span>
          )}
          {tvlChange7d !== undefined && (
            <span className={`text-xs font-mono ${tvlChange7d >= 0 ? 'text-lime-600 dark:text-lime-400' : 'text-red-600 dark:text-red-400'}`}>
              7d: {tvlChange7d >= 0 ? '+' : ''}{tvlChange7d.toFixed(1)}%
            </span>
          )}
        </div>
      </div>

      {/* Chart */}
      <svg
        ref={chartRef}
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        preserveAspectRatio="none"
        className="w-full h-40"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Gradient definitions */}
        <defs>
          <linearGradient id="tvlAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.25" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0.25, 0.5, 0.75].map((ratio) => (
          <line
            key={ratio}
            x1={padding.left}
            y1={padding.top + (viewBoxHeight - padding.top - padding.bottom) * ratio}
            x2={viewBoxWidth - padding.right}
            y2={padding.top + (viewBoxHeight - padding.top - padding.bottom) * ratio}
            className="stroke-gray-200 dark:stroke-neutral-800"
            strokeWidth="1"
            strokeDasharray="4,4"
          />
        ))}

        {/* Area fill */}
        <path
          d={chartData.areaPath}
          fill="url(#tvlAreaGradient)"
        />

        {/* Line */}
        <path
          d={chartData.linePath}
          fill="none"
          stroke={strokeColor}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Hover indicator */}
        {hoveredPoint && (
          <>
            <line
              x1={hoveredPoint.x}
              y1={padding.top}
              x2={hoveredPoint.x}
              y2={viewBoxHeight - padding.bottom}
              className="stroke-gray-400 dark:stroke-neutral-600"
              strokeWidth="1"
              strokeDasharray="4,4"
            />
            <circle
              cx={hoveredPoint.x}
              cy={hoveredPoint.y}
              r="5"
              fill={strokeColor}
              className="drop-shadow-sm"
            />
          </>
        )}

        {/* X-axis labels */}
        <text
          x={padding.left}
          y={viewBoxHeight - 4}
          className="fill-gray-500 dark:fill-neutral-500"
          style={{ fontSize: '11px', fontFamily: 'monospace' }}
        >
          {formatDate(data[0].date)}
        </text>
        <text
          x={viewBoxWidth - padding.right}
          y={viewBoxHeight - 4}
          textAnchor="end"
          className="fill-gray-500 dark:fill-neutral-500"
          style={{ fontSize: '11px', fontFamily: 'monospace' }}
        >
          {formatDate(data[data.length - 1].date)}
        </text>
      </svg>

      {/* Footer */}
      <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-100 dark:border-neutral-800">
        <span className="text-[9px] font-mono text-gray-400 dark:text-neutral-600">
          30d • DeFiLlama
        </span>
        <a
          href={defiLlamaHref}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[9px] font-mono text-teal-600 dark:text-teal-400 hover:underline"
        >
          More →
        </a>
      </div>
    </div>
  );
}

export default ChainTvlChart;
